import { memo } from 'react';

import { Zap } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

import { Button } from '@/shadcn/button';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from '@/shadcn/hover-card';
import { cn } from '@/shadcn/lib/utils';

import { isFilterablePath } from '@/utils/filtering/filtering.util';
import type { SortSetup } from '@/utils/sorting/sorting.types';
import { getSortConfig } from '../sort';
import type { SortConfig, TableColumnTransformed } from '../table.type';
import { onSortIconClick } from './colHeaders.utils';
import { SortIcon } from './sortIcon.colHeaders';

type Props = {
  height: number;
  selectBoxWidth?: number;
  columns: TableColumnTransformed[];
  sortSetup?: SortSetup;
  setSortSetup: (sortSetup: SortSetup | null, sortConfig: SortConfig) => void;
  disableClientSideSortFilter?: boolean;
};

export const ColHeaders = memo(
  ({
    height,
    selectBoxWidth = 0,
    columns,
    sortSetup,
    setSortSetup,
    disableClientSideSortFilter = false
  }: Props) => {
    const sortThisColumn = colConfig => {
      onSortIconClick(colConfig, sortSetup, setSortSetup);
    };
    return (
      <div className="flex w-fit flex-row flex-nowrap border-border border-b-2 bg-card text-left text-[10px] text-muted-foreground tracking-wide">
        {selectBoxWidth > 0 && (
          <div
            className="border-border border-r"
            style={{
              width: selectBoxWidth,
              height
            }}
          />
        )}
        {columns.map((colConfig, index) => {
          const isSorted = sortSetup && sortSetup.key === colConfig.key;
          const isSortable =
            !disableClientSideSortFilter ||
            isFilterablePath(getSortConfig(colConfig)?.path);

          return (
            <div
              className={cn(
                'box-border flex flex-row flex-nowrap items-center justify-between border-border border-r py-[4px] pl-2',
                isSorted && 'bg-accent text-accent-foreground!'
              )}
              key={index + (colConfig?.def?.header ?? '')}
              style={{
                width: colConfig.width,
                height
              }}
            >
              <div className="flex items-center gap-2 overflow-hidden">
                <div className="truncate text-muted-foreground uppercase">
                  {colConfig.def?.header}
                </div>
                {colConfig?.def?.isDynamic && (
                  <HoverCard openDelay={0}>
                    <HoverCardTrigger className="cursor-pointer">
                      <Button className="p-0" size="xsIcon" variant="ghost">
                        <Zap
                          className="text-secondary dark:text-primary"
                          fill="currentColor"
                          size={11}
                        />
                      </Button>
                    </HoverCardTrigger>
                    <HoverCardContent className="flex flex-nowrap items-start gap-4 text-sm">
                      <Zap
                        className="mt-0.5 shrink-0 text-secondary dark:text-primary"
                        fill="currentColor"
                        size={16}
                      />
                      <div className="text-xs">
                        <ReactMarkdown>
                          {colConfig.def?.dynamicHelper ??
                            `\`${colConfig.def?.header}\` is derived dynamically from a different value.`}
                        </ReactMarkdown>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                )}
              </div>
              {isSortable && (
                <Button
                  className="mr-1"
                  data-cy={`sort-${colConfig.key}`}
                  onClick={() => sortThisColumn(colConfig)}
                  size="smallIcon"
                  variant="ghost"
                >
                  <SortIcon
                    direction={sortSetup?.direction}
                    isSorted={isSorted}
                    size={15}
                  />
                </Button>
              )}
            </div>
          );
        })}
      </div>
    );
  }
);
